/**
 * DataLanguageContext
 * Controls whether user-entered free text (product names, etc.) is shown
 * translated into the UI language or left as originally entered.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useUserPreferences } from '../hooks/useUserPreferences';

const STORAGE_KEY = 'loyal.show_original_data';

interface DataLanguageContextValue {
  showOriginal: boolean;
  setShowOriginal: (value: boolean) => void;
}

const DataLanguageContext = createContext<DataLanguageContextValue | null>(null);

function readLocalFlag(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLocalFlag(value: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    // ignore quota / private mode
  }
}

export function DataLanguageProvider({ children }: { children: ReactNode }) {
  const { preferences, updatePreferences, isLoading } = useUserPreferences();
  const [showOriginal, setShowOriginalState] = useState<boolean>(() => readLocalFlag());

  // Sync from server preference once loaded (server wins over stale local)
  useEffect(() => {
    if (isLoading) return;
    if (typeof preferences.show_original_data === 'boolean') {
      setShowOriginalState(preferences.show_original_data);
      writeLocalFlag(preferences.show_original_data);
    }
  }, [isLoading, preferences.show_original_data]);

  const setShowOriginal = useCallback(
    (value: boolean) => {
      setShowOriginalState(value);
      writeLocalFlag(value);
      // PUT /preferences replaces the whole object — merge with current prefs
      updatePreferences({ ...preferences, show_original_data: value }).catch(() => {
        // local flag already applied
      });
    },
    [updatePreferences, preferences]
  );

  const value = useMemo(() => ({ showOriginal, setShowOriginal }), [showOriginal, setShowOriginal]);

  return <DataLanguageContext.Provider value={value}>{children}</DataLanguageContext.Provider>;
}

export function useDataLanguage(): DataLanguageContextValue {
  const ctx = useContext(DataLanguageContext);
  if (!ctx) {
    // Safe default when used outside the provider (tests, isolated stories)
    return {
      showOriginal: false,
      setShowOriginal: () => undefined,
    };
  }
  return ctx;
}
